import { Box, CircularProgress, useTheme } from '@mui/material';
import { useCreateExercise, useGetExerciseById, useUpdateExercise } from '_api';
import { Exercise, ExercisePayload } from '_api/types';
import { AdminRouteMap } from '_constants';
import { SEVERITY } from '_constants/derived';
import { FormHeaderText, TextButton } from 'components/atoms';
import { AlertMessage } from 'components/molecules';
import { ExerciseForm } from 'components/organisms/forms';
import { InfoSimpleModal } from 'components/organisms/modals';
import React, { useEffect, useMemo, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { useNavigateTo } from 'utils';
import { useExercise, useUser } from 'utils/hooks';

const convertToExercisePayload = (initExercise: Exercise) => {
  const {
    fullImageUrl: _fullImageUrl,
    fullVideoUrl: _fullVideoUrl,
    ...excr
  } = initExercise;
  return {
    ...excr,
    category: excr.category ? excr.category.map((cate) => cate.id) : [],
    equipment: excr.equipment ? excr.equipment.map((equip) => equip.id) : [],
    targetSide: excr.targetSide.name,
  } as ExercisePayload;
};

export const EditExercise = () => {
  const navigate = useNavigateTo();
  const theme = useTheme();
  const { exerciseId: id } = useParams();
  const exerciseId = useMemo(() => (id !== undefined ? Number(id) : 0), [id]);
  const { data: apiExercise, isLoading } = useGetExerciseById(exerciseId);
  const { mutateAsync: createExercise } = useCreateExercise();
  const { mutateAsync: updateExercise } = useUpdateExercise();
  const { canEditObject } = useUser();
  const { allExercise } = useExercise();

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [exercise, setExercise] = useState<ExercisePayload | undefined>(
    apiExercise ? convertToExercisePayload(apiExercise) : undefined,
  );
  const [openEditConfirm, setOpenEditConfirm] = useState(false);
  const [openCopyConfirm, setOpenCopyConfirm] = useState(false);
  const [payload, setPayload] = useState<ExercisePayload>(exercise as ExercisePayload);
  const changingSharedToUnshared = useMemo(
    () =>
      apiExercise && apiExercise.shareWithOrg && payload ? !payload.shareWithOrg : false,
    [apiExercise, payload],
  );
  const userCanEditExercise = apiExercise ? canEditObject(apiExercise) : true;

  useEffect(() => {
    if (id && apiExercise) setExercise(convertToExercisePayload(apiExercise));
  }, [apiExercise, id]);

  useEffect(() => setPayload(exercise as ExercisePayload), [exercise]);

  const onUpdate = async (key: any, arg: any) => {
    setPayload((current) => ({
      ...(current as ExercisePayload),
      [key]: arg,
    }));
  };

  const checkTitleChange = (title: string) => {
    if (allExercise && allExercise.filter((ex) => ex.title === title).length > 0) {
      setErrorMessage(
        'Must change exercise title - current title already exists on a different exercise.',
      );
      return false;
    }
    return true;
  };

  const onCopy = () => {
    setOpenCopyConfirm(false);
    if (checkTitleChange(payload.title)) {
      createExercise(payload)
        .then((data) => {
          if (data && data.id) {
            setSuccessMessage('Successfully created a new copy of this exercise.');
            setTimeout(() => {
              navigate(AdminRouteMap.LIBRARY);
            }, 1500);
          }
        })
        .catch((err) => setErrorMessage(err.message));
    }
  };

  const onBack = () => {
    history.back();
  };

  const onSubmit = () => {
    setOpenEditConfirm(false);
    updateExercise(payload)
      .then((data) => {
        if (data && data.id) {
          setSuccessMessage('Successfully updated exercise.');
          setTimeout(() => {
            navigate(AdminRouteMap.LIBRARY);
          }, 1500);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          setErrorMessage(
            'Unexpected error occured. Please refresh the page and try again.',
          );
        } else if (error.message) {
          setErrorMessage(`ERROR: ${error.message}`);
        } else {
          setErrorMessage('Unexpected error. Please Try again.');
        }
      });
  };

  return (
    <Box>
      <Box>
        <FormHeaderText gutterBottom> Edit Exercise </FormHeaderText>
        <TextButton
          onClick={onBack}
          sx={{ color: theme.palette.primary.main, marginBottom: '10px', paddingLeft: 0 }}
        >
          <IoArrowBackOutline />
          BACK
        </TextButton>
      </Box>
      {!isLoading && exercise && exercise.id === exerciseId ? (
        <Box>
          <ExerciseForm
            disableAll={!!successMessage}
            disableSubmit={changingSharedToUnshared || !userCanEditExercise}
            initValue={exercise}
            onBack={onBack}
            onSubmit={() => setOpenEditConfirm(true)}
            onCopy={() => setOpenCopyConfirm(true)}
            onUpdate={onUpdate}
            submitLabel={'Confirm Edit Changes'}
            disabledSubmitTooltip={
              !userCanEditExercise
                ? 'cannot edit exercises you did not create'
                : changingSharedToUnshared
                  ? 'shared exercies cannot be changed to not shared'
                  : ''
            }
          />
        </Box>
      ) : (
        <CircularProgress />
      )}
      <AlertMessage
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
      <AlertMessage
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={errorMessage}
      />
      <InfoSimpleModal
        icon={FaCheck}
        open={openEditConfirm}
        onClose={() => setOpenEditConfirm(false)}
        onSubmit={onSubmit}
        title={'Confirm Edit'}
        text={[
          `You are about to edit this exercise.`,
          ` Are you sure you want to do this?`,
        ]}
      />
      <InfoSimpleModal
        icon={FaCheck}
        open={openCopyConfirm}
        onClose={() => setOpenCopyConfirm(false)}
        onSubmit={onCopy}
        title={'Create Copy'}
        text={[
          `You are about to create a new exercise that is a copy of this current exercise.`,
          ` Are you sure you want to do this?`,
        ]}
      />
    </Box>
  );
};
